// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Card,
  Text,
  Group,
  Stack,
  Badge,
  Table,
  Select,
  Grid,
  RingProgress,
  Progress,
  Tabs,
  Switch,
  Loader,
  Title,
  Paper,} from '@mantine/core';
import {
  IconChartLine,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconClock,
  IconCheck,
  IconX,
  IconActivity,
} from '@tabler/icons-react';
import { usePolicyAnalytics, type PolicyAnalytics } from '../hooks/usePolicyAnalytics';
import {  Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from 'recharts';
interface PolicyAnalyticsDashboardProps {
  policyId?: string;
  showSummary?: boolean;
}

export const PolicyAnalyticsDashboard: React.FC<PolicyAnalyticsDashboardProps> = ({
  policyId: initialPolicyId,
  showSummary = false,
}) => {
  const {
    analytics,
    usageTrend,
    evaluationHistory,
    summary,
    loading,
    autoRefreshEnabled,
    loadPolicyAnalytics,
    loadUsageTrend,
    loadEvaluationHistory,
    loadSummary,
    startAutoRefresh,
    stopAutoRefresh,
  } = usePolicyAnalytics();

  const [selectedPolicyId, setSelectedPolicyId] = useState<string | undefined>(initialPolicyId);
  const [timeRange, setTimeRange] = useState<string>('7d');
  const [activeTab, setActiveTab] = useState<string>('overview');

  // ============================================================================
  // Load Data on Mount and Policy/Time Range Change
  // ============================================================================

  useEffect(() => {
    if (showSummary) {
      loadSummary(timeRange);
    } else if (selectedPolicyId) {
      loadPolicyAnalytics(selectedPolicyId, timeRange);
      loadUsageTrend(selectedPolicyId, timeRange);
      loadEvaluationHistory(selectedPolicyId, 50);
    }
  }, [selectedPolicyId, timeRange, showSummary, loadPolicyAnalytics, loadUsageTrend, loadEvaluationHistory, loadSummary]);

  // ============================================================================
  // Auto-Refresh Toggle Handler
  // ============================================================================

  const handleAutoRefreshToggle = (checked: boolean) => {
    if (checked && selectedPolicyId) {
      startAutoRefresh(selectedPolicyId, 30000); // 30 seconds
    } else {
      stopAutoRefresh();
    }
  };

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <IconTrendingUp size={16} color="green" />;
      case 'down':
        return <IconTrendingDown size={16} color="red" />;
      case 'stable':
        return <IconMinus size={16} color="gray" />;
    }
  };

  const getEffectivenessColor = (score: number): string => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    if (score >= 40) return 'orange';
    return 'red';
  };

  // ============================================================================
  // Summary View (All Policies)
  // ============================================================================

  if (showSummary) {
    return (
      <Card shadow="sm" padding="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={3}>Policy Analytics Summary</Title>
            <Select
              value={timeRange}
              onChange={(value) => setTimeRange(value || '7d')}
              data={[
                { value: '1d', label: 'Last 24 Hours' },
                { value: '7d', label: 'Last 7 Days' },
                { value: '30d', label: 'Last 30 Days' },
                { value: '90d', label: 'Last 90 Days' },
              ]}
              style={{ width: 150 }}
            />
          </Group>

          {loading && !summary ? (
            <Group justify="center" py={40}>
              <Loader />
            </Group>
          ) : summary ? (
            <>
              {/* Summary Cards */}
              <Grid>
                <Grid.Col span={3}>
                  <Paper shadow="sm" p="md" withBorder>
                    <Stack gap="xs">
                      <Text size="sm" color="dimmed">
                        Total Policies
                      </Text>
                      <Text size={32} fw={700}>
                        {summary.total_policies}
                      </Text>
                      <Text size="xs" color="dimmed">
                        {summary.active_policies} active
                      </Text>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Paper shadow="sm" p="md" withBorder>
                    <Stack gap="xs">
                      <Text size="sm" color="dimmed">
                        Total Evaluations
                      </Text>
                      <Text size={32} fw={700}>
                        {summary.total_evaluations.toLocaleString()}
                      </Text>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Paper shadow="sm" p="md" withBorder>
                    <Stack gap="xs">
                      <Text size="sm" color="dimmed">
                        Avg Effectiveness
                      </Text>
                      <Group ta="right" gap="xs">
                        <Text size={32} fw={700}>
                          {summary.avg_effectiveness.toFixed(1)}%
                        </Text>
                        <RingProgress
                          size={60}
                          thickness={6}
                          sections={[
                            {
                              value: summary.avg_effectiveness,
                              color: getEffectivenessColor(summary.avg_effectiveness),
                            },
                          ]}
                        />
                      </Group>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={3}>
                  <Paper shadow="sm" p="md" withBorder>
                    <Stack gap="xs">
                      <Text size="sm" color="dimmed">
                        Performance by Type
                      </Text>
                      <Text size="sm" fw={600}>
                        {Object.keys(summary.performance_by_type).length} types
                      </Text>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </Grid>

              {/* Top Policies Table */}
              <Card shadow="sm" padding="md" mt="md">
                <Text size="lg" fw={600} mb="md">
                  Most Used Policies
                </Text>
                <Table>
                  <thead>
                    <tr>
                      <th>Policy Name</th>
                      <th>Evaluations</th>
                      <th>Effectiveness</th>
                      <th>Avg Time (ms)</th>
                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.most_used_policies.map((policy) => (
                      <tr key={policy.policy_id}>
                        <td>{policy.policy_name}</td>
                        <td>{policy.evaluation_count.toLocaleString()}</td>
                        <td>
                          <Badge color={getEffectivenessColor(policy.effectiveness_score)}>
                            {policy.effectiveness_score.toFixed(1)}%
                          </Badge>
                        </td>
                        <td>{policy.avg_evaluation_time_ms.toFixed(2)}</td>
                        <td>{getTrendIcon(policy.trend)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>

              {/* Least Effective Policies Table */}
              {summary.least_effective_policies.length > 0 && (
                <Card shadow="sm" padding="md" mt="md">
                  <Text size="lg" fw={600} mb="md" color="orange">
                    Policies Needing Review
                  </Text>
                  <Table>
                    <thead>
                      <tr>
                        <th>Policy Name</th>
                        <th>Evaluations</th>
                        <th>Effectiveness</th>
                        <th>Matched</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.least_effective_policies.map((policy) => (
                        <tr key={policy.policy_id}>
                          <td>{policy.policy_name}</td>
                          <td>{policy.evaluation_count.toLocaleString()}</td>
                          <td>
                            <Badge color={getEffectivenessColor(policy.effectiveness_score)}>
                              {policy.effectiveness_score.toFixed(1)}%
                            </Badge>
                          </td>
                          <td>
                            {policy.matched_count} / {policy.evaluation_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              )}

              {/* Performance by Type Chart */}
              <Card shadow="sm" padding="md" mt="md">
                <Text size="lg" fw={600} mb="md">
                  Performance by Policy Type
                </Text>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={Object.entries(summary.performance_by_type).map(([type, avgTime]) => ({
                      type,
                      avgTime,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" />
                    <YAxis />
                    <ChartTooltip />
                    <Legend />
                    <Bar dataKey="avgTime" fill="#8884d8" name="Avg Evaluation Time (ms)" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </>
          ) : (
            <Text color="dimmed" ta="center" py={40}>
              No analytics data available
            </Text>
          )}
        </Stack>
      </Card>
    );
  }

  // ============================================================================
  // Single Policy View
  // ============================================================================

  if (!selectedPolicyId) {
    return (
      <Card shadow="sm" padding="lg">
        <Text color="dimmed" ta="center">
          Select a policy to view analytics
        </Text>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconChartLine size={24} />
            <Title order={3}>Policy Analytics</Title>
          </Group>
          <Group gap="md">
            <Select
              value={timeRange}
              onChange={(value) => setTimeRange(value || '7d')}
              data={[
                { value: '1d', label: 'Last 24 Hours' },
                { value: '7d', label: 'Last 7 Days' },
                { value: '30d', label: 'Last 30 Days' },
                { value: '90d', label: 'Last 90 Days' },
              ]}
              style={{ width: 150 }}
            />
            <Switch
              label="Auto-refresh"
              checked={autoRefreshEnabled}
              onChange={(e) => handleAutoRefreshToggle(e.currentTarget.checked)}
            />
          </Group>
        </Group>

        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconActivity size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="trending" leftSection={<IconChartLine size={16} />}>
              Trending
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconClock size={16} />}>
              History
            </Tabs.Tab>
          </Tabs.List>

          {/* ====================================================================== */}
          {/* Overview Tab */}
          {/* ====================================================================== */}
          <Tabs.Panel value="overview" pt="md">
            {loading && !analytics ? (
              <Group justify="center" py={40}>
                <Loader />
              </Group>
            ) : analytics ? (
              <>
                {/* Key Metrics Cards */}
                <Grid>
                  <Grid.Col span={3}>
                    <Paper shadow="sm" p="md" withBorder>
                      <Stack gap="xs">
                        <Text size="sm" color="dimmed">
                          Total Evaluations
                        </Text>
                        <Text size={32} fw={700}>
                          {analytics.evaluation_count.toLocaleString()}
                        </Text>
                        {getTrendIcon(analytics.trend)}
                      </Stack>
                    </Paper>
                  </Grid.Col>

                  <Grid.Col span={3}>
                    <Paper shadow="sm" p="md" withBorder>
                      <Stack gap="xs">
                        <Text size="sm" color="dimmed">
                          Matched
                        </Text>
                        <Group ta="right" gap="xs">
                          <Text size={32} fw={700} color="green">
                            {analytics.matched_count}
                          </Text>
                          <IconCheck size={20} color="green" />
                        </Group>
                        <Progress
                          value={(analytics.matched_count / analytics.evaluation_count) * 100}
                          color="green"
                        />
                      </Stack>
                    </Paper>
                  </Grid.Col>

                  <Grid.Col span={3}>
                    <Paper shadow="sm" p="md" withBorder>
                      <Stack gap="xs">
                        <Text size="sm" color="dimmed">
                          Effectiveness Score
                        </Text>
                        <Group ta="right" gap="xs">
                          <Text size={32} fw={700}>
                            {analytics.effectiveness_score.toFixed(1)}%
                          </Text>
                          <RingProgress
                            size={60}
                            thickness={6}
                            sections={[
                              {
                                value: analytics.effectiveness_score,
                                color: getEffectivenessColor(analytics.effectiveness_score),
                              },
                            ]}
                          />
                        </Group>
                      </Stack>
                    </Paper>
                  </Grid.Col>

                  <Grid.Col span={3}>
                    <Paper shadow="sm" p="md" withBorder>
                      <Stack gap="xs">
                        <Text size="sm" color="dimmed">
                          Avg Eval Time
                        </Text>
                        <Group ta="right" gap="xs">
                          <Text size={32} fw={700}>
                            {analytics.avg_evaluation_time_ms.toFixed(2)}
                          </Text>
                          <Text size="sm" color="dimmed">
                            ms
                          </Text>
                        </Group>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                </Grid>

                {/* Actions Breakdown */}
                <Grid mt="md">
                  <Grid.Col span={6}>
                    <Paper shadow="sm" p="md" withBorder>
                      <Stack gap="xs">
                        <Text size="md" fw={600}>
                          Blocked Actions
                        </Text>
                        <Group ta="right" gap="xs">
                          <Text size={28} fw={700} color="red">
                            {analytics.blocked_count}
                          </Text>
                          <IconX size={20} color="red" />
                        </Group>
                        <Progress
                          value={(analytics.blocked_count / analytics.evaluation_count) * 100}
                          color="red"
                        />
                      </Stack>
                    </Paper>
                  </Grid.Col>

                  <Grid.Col span={6}>
                    <Paper shadow="sm" p="md" withBorder>
                      <Stack gap="xs">
                        <Text size="md" fw={600}>
                          Approved Actions
                        </Text>
                        <Group ta="right" gap="xs">
                          <Text size={28} fw={700} color="blue">
                            {analytics.approved_count}
                          </Text>
                          <IconCheck size={20} color="blue" />
                        </Group>
                        <Progress
                          value={(analytics.approved_count / analytics.evaluation_count) * 100}
                          color="blue"
                        />
                      </Stack>
                    </Paper>
                  </Grid.Col>
                </Grid>

                {/* Last Evaluated */}
                <Paper shadow="sm" p="md" withBorder mt="md">
                  <Group justify="space-between">
                    <Text size="sm" color="dimmed">
                      Last Evaluated
                    </Text>
                    <Text size="sm" fw={600}>
                      {new Date(analytics.last_evaluated).toLocaleString()}
                    </Text>
                  </Group>
                </Paper>
              </>
            ) : (
              <Text color="dimmed" ta="center" py={40}>
                No analytics data available for this policy
              </Text>
            )}
          </Tabs.Panel>

          {/* ====================================================================== */}
          {/* Trending Tab */}
          {/* ====================================================================== */}
          <Tabs.Panel value="trending" pt="md">
            {loading && usageTrend.length === 0 ? (
              <Group justify="center" py={40}>
                <Loader />
              </Group>
            ) : usageTrend.length > 0 ? (
              <>
                {/* Evaluation Count Chart */}
                <Card shadow="sm" padding="md">
                  <Text size="md" fw={600} mb="md">
                    Evaluation Trend
                  </Text>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={usageTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(time) => new Date(time).toLocaleDateString()}
                      />
                      <YAxis />
                      <ChartTooltip
                        labelFormatter={(time: any) => new Date(time).toLocaleString()}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="evaluation_count"
                        stroke="#8884d8"
                        name="Evaluations"
                      />
                      <Line
                        type="monotone"
                        dataKey="matched_count"
                        stroke="#82ca9d"
                        name="Matched"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                {/* Performance Chart */}
                <Card shadow="sm" padding="md" mt="md">
                  <Text size="md" fw={600} mb="md">
                    Performance Trend
                  </Text>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={usageTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(time) => new Date(time).toLocaleDateString()}
                      />
                      <YAxis />
                      <ChartTooltip
                        labelFormatter={(time: any) => new Date(time).toLocaleString()}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="avg_evaluation_time_ms"
                        stroke="#ff7300"
                        name="Avg Eval Time (ms)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </>
            ) : (
              <Text color="dimmed" ta="center" py={40}>
                No trend data available
              </Text>
            )}
          </Tabs.Panel>

          {/* ====================================================================== */}
          {/* History Tab */}
          {/* ====================================================================== */}
          <Tabs.Panel value="history" pt="md">
            {loading && evaluationHistory.length === 0 ? (
              <Group justify="center" py={40}>
                <Loader />
              </Group>
            ) : evaluationHistory.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Matched</th>
                    <th>Eval Time (ms)</th>
                    <th>Triggered Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {evaluationHistory.map((evaluation) => (
                    <tr key={evaluation.evaluation_id}>
                      <td>{new Date(evaluation.timestamp).toLocaleString()}</td>
                      <td>
                        {evaluation.matched ? (
                          <Badge color="green" leftSection={<IconCheck size={12} />}>
                            Matched
                          </Badge>
                        ) : (
                          <Badge color="gray" leftSection={<IconX size={12} />}>
                            No Match
                          </Badge>
                        )}
                      </td>
                      <td>{evaluation.evaluation_time_ms.toFixed(2)}</td>
                      <td>
                        {evaluation.triggered_actions.length > 0 ? (
                          <Group gap="xs">
                            {evaluation.triggered_actions.map((action, idx) => (
                              <Badge key={idx} size="sm" variant="outline">
                                {action}
                              </Badge>
                            ))}
                          </Group>
                        ) : (
                          <Text size="sm" color="dimmed">
                            None
                          </Text>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Text color="dimmed" ta="center" py={40}>
                No evaluation history available
              </Text>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Card>
  );
};
